import { Check } from "lucide-react";

/**
 * Generic comparison table — used for "Occasion vs Reconditionné vs Neuf" on the échange-standard page.
 * One column can be highlighted (the recommended option). Content:
 * { title, subtitle, columns: string[], rows: [{ label, cells: string[] }], highlightColumn?: number }.
 */
type Row = { label?: string; cells?: string[] };

export function ComparisonTableRenderer({
  component,
}: {
  component: { content?: { title?: string; subtitle?: string; columns?: string[]; rows?: Row[]; highlightColumn?: number } };
}) {
  const { title, subtitle, columns = [], rows = [], highlightColumn } = component.content ?? {};
  if (columns.length === 0 || rows.length === 0) return null;
  const hi = typeof highlightColumn === "number" ? highlightColumn : -1;

  return (
    <section className="mx-auto max-w-5xl px-4 py-10 md:px-8 md:py-14">
      {title ? (
        <div className="mb-6 text-center">
          <h2 className="text-2xl font-bold tracking-tight text-[#0A0A0A] md:text-[2rem]" style={{ fontFamily: "var(--font-aleo)" }}>{title}</h2>
          {subtitle ? <p className="mt-2 text-[#6B7280]">{subtitle}</p> : null}
          <div className="mx-auto mt-3 h-1 w-10 rounded-full bg-[#00B862]" aria-hidden="true" />
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-2xl shadow-[var(--shadow-e1)] ring-1 ring-black/10">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr>
              <th className="bg-[#0A0A0A] px-4 py-3.5 text-left font-semibold text-white/60" />
              {columns.map((c, i) => (
                <th
                  key={i}
                  className={`px-4 py-3.5 text-center font-bold text-white ${i === hi ? "bg-[#0A0A0A] shadow-[inset_0_2px_0_#00B862]" : "bg-[#0A0A0A]"}`}
                >
                  {i === hi ? (
                    <span className="inline-flex items-center gap-1.5 text-[#34D98A]"><Check className="h-4 w-4" /> {c}</span>
                  ) : c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={ri} className="border-t border-black/5 odd:bg-[var(--color-surface-1)]">
                <th className="px-4 py-3 text-left font-semibold text-[#121212]">{r.label}</th>
                {(r.cells ?? []).map((cell, ci) => (
                  <td
                    key={ci}
                    className={`px-4 py-3 text-center ${ci === hi ? "bg-[var(--color-forest-100)]/50 font-semibold text-[var(--color-forest-700)]" : "text-[#6B7280]"}`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
